// The standard security handler (ISO 32000-1 §7.6, ISO 32000-2): RC4 40–128 bit, AES-128 and AES-256, opened with the empty user password.

import { Dict, PName, type Cipher, type PObj, type XRef, PRef } from "./parse";

// ---- MD5 ------------------------------------------------------------------------------------------------------------------------------
const K = new Uint32Array(64).map((_, i) => Math.floor(Math.abs(Math.sin(i + 1)) * 4294967296) >>> 0);
const S = [7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21];

export function md5(data: Uint8Array): Uint8Array {
    const n = data.length;
    const padded = new Uint8Array(((n + 8) >> 6 << 6) + 64);
    padded.set(data);
    padded[n] = 0x80;
    const dv = new DataView(padded.buffer);
    dv.setUint32(padded.length - 8, (n << 3) >>> 0, true);
    dv.setUint32(padded.length - 4, Math.floor(n / 0x20000000), true);
    let a0 = 0x67452301, b0 = 0xefcdab89, c0 = 0x98badcfe, d0 = 0x10325476;
    const M = new Uint32Array(16);
    for (let off = 0; off < padded.length; off += 64) {
        for (let i = 0; i < 16; i++) M[i] = dv.getUint32(off + i * 4, true);
        let A = a0, B = b0, C = c0, D = d0;
        for (let i = 0; i < 64; i++) {
            let F: number, g: number;
            if (i < 16) { F = (B & C) | (~B & D); g = i; }
            else if (i < 32) { F = (D & B) | (~D & C); g = (5 * i + 1) & 15; }
            else if (i < 48) { F = B ^ C ^ D; g = (3 * i + 5) & 15; }
            else { F = C ^ (B | ~D); g = (7 * i) & 15; }
            F = (F + A + K[i]! + M[g]!) >>> 0;
            A = D; D = C; C = B;
            B = (B + ((F << S[i]!) | (F >>> (32 - S[i]!)))) >>> 0;
        }
        a0 = (a0 + A) >>> 0; b0 = (b0 + B) >>> 0; c0 = (c0 + C) >>> 0; d0 = (d0 + D) >>> 0;
    }
    const out = new Uint8Array(16);
    const o = new DataView(out.buffer);
    o.setUint32(0, a0, true); o.setUint32(4, b0, true); o.setUint32(8, c0, true); o.setUint32(12, d0, true);
    return out;
}

// ---- RC4 ------------------------------------------------------------------------------------------------------------------------------
export function rc4(key: Uint8Array, data: Uint8Array): Uint8Array {
    const s = new Uint8Array(256);
    for (let i = 0; i < 256; i++) s[i] = i;
    for (let i = 0, j = 0; i < 256; i++) { j = (j + s[i]! + key[i % key.length]!) & 255; [s[i], s[j]] = [s[j]!, s[i]!]; }
    const out = new Uint8Array(data.length);
    let i = 0, j = 0;
    for (let k = 0; k < data.length; k++) {
        i = (i + 1) & 255; j = (j + s[i]!) & 255;
        [s[i], s[j]] = [s[j]!, s[i]!];
        out[k] = data[k]! ^ s[(s[i]! + s[j]!) & 255]!;
    }
    return out;
}

// ---- AES (decrypt, CBC) and encrypt for the R6 hash -----------------------------------------------------------------------------------
const SBOX = new Uint8Array(256);
const INV = new Uint8Array(256);
(() => {
    let p = 1, q = 1;
    do {
        p = p ^ ((p << 1) & 255) ^ (p & 0x80 ? 0x1b : 0);
        q ^= q << 1; q ^= q << 2; q ^= q << 4; q &= 255; if (q & 0x80) q ^= 0x09;
        const x = q ^ ((q << 1) | (q >> 7)) ^ ((q << 2) | (q >> 6)) ^ ((q << 3) | (q >> 5)) ^ ((q << 4) | (q >> 4));
        SBOX[p] = (x ^ 0x63) & 255;
    } while (p !== 1);
    SBOX[0] = 0x63;
    for (let i = 0; i < 256; i++) INV[SBOX[i]!] = i;
})();

const xt = (a: number) => ((a << 1) ^ (a & 0x80 ? 0x1b : 0)) & 255;
const mul = (a: number, b: number) => { let r = 0; while (b) { if (b & 1) r ^= a; a = xt(a); b >>= 1; } return r; };

function expandKey(key: Uint8Array): { rk: Uint8Array; rounds: number } {
    const nk = key.length / 4, rounds = nk + 6;
    const w = new Uint8Array(16 * (rounds + 1));
    w.set(key);
    let rcon = 1;
    for (let i = nk; i < 4 * (rounds + 1); i++) {
        const t = [w[(i - 1) * 4]!, w[(i - 1) * 4 + 1]!, w[(i - 1) * 4 + 2]!, w[(i - 1) * 4 + 3]!];
        if (i % nk === 0) { t.push(t.shift()!); for (let k = 0; k < 4; k++) t[k] = SBOX[t[k]!]!; t[0]! ^= rcon; rcon = xt(rcon); }
        else if (nk > 6 && i % nk === 4) for (let k = 0; k < 4; k++) t[k] = SBOX[t[k]!]!;
        for (let k = 0; k < 4; k++) w[i * 4 + k] = w[(i - nk) * 4 + k]! ^ t[k]!;
    }
    return { rk: w, rounds };
}

function decryptBlock(rk: Uint8Array, rounds: number, inp: Uint8Array, o: number, out: Uint8Array) {
    const s = new Uint8Array(16);
    for (let i = 0; i < 16; i++) s[i] = inp[o + i]! ^ rk[rounds * 16 + i]!;
    for (let r = rounds - 1; r >= 0; r--) {
        // inverse shift rows
        const t = new Uint8Array(16);
        for (let c = 0; c < 4; c++) for (let rw = 0; rw < 4; rw++) t[((c + rw) & 3) * 4 + rw] = s[c * 4 + rw]!;
        for (let i = 0; i < 16; i++) s[i] = INV[t[i]!]! ^ rk[r * 16 + i]!;
        if (r > 0) {
            for (let c = 0; c < 4; c++) {
                const a = [s[c * 4]!, s[c * 4 + 1]!, s[c * 4 + 2]!, s[c * 4 + 3]!];
                s[c * 4] = mul(a[0]!, 14) ^ mul(a[1]!, 11) ^ mul(a[2]!, 13) ^ mul(a[3]!, 9);
                s[c * 4 + 1] = mul(a[0]!, 9) ^ mul(a[1]!, 14) ^ mul(a[2]!, 11) ^ mul(a[3]!, 13);
                s[c * 4 + 2] = mul(a[0]!, 13) ^ mul(a[1]!, 9) ^ mul(a[2]!, 14) ^ mul(a[3]!, 11);
                s[c * 4 + 3] = mul(a[0]!, 11) ^ mul(a[1]!, 13) ^ mul(a[2]!, 9) ^ mul(a[3]!, 14);
            }
        }
    }
    out.set(s);
}

function encryptBlock(rk: Uint8Array, rounds: number, inp: Uint8Array, o: number, out: Uint8Array) {
    const s = new Uint8Array(16);
    for (let i = 0; i < 16; i++) s[i] = inp[o + i]! ^ rk[i]!;
    for (let r = 1; r <= rounds; r++) {
        const t = new Uint8Array(16);
        for (let i = 0; i < 16; i++) t[i] = SBOX[s[i]!]!;
        for (let c = 0; c < 4; c++) for (let rw = 0; rw < 4; rw++) s[c * 4 + rw] = t[((c + rw) & 3) * 4 + rw]!;
        if (r < rounds) {
            for (let c = 0; c < 4; c++) {
                const a = [s[c * 4]!, s[c * 4 + 1]!, s[c * 4 + 2]!, s[c * 4 + 3]!];
                s[c * 4] = xt(a[0]!) ^ (xt(a[1]!) ^ a[1]!) ^ a[2]! ^ a[3]!;
                s[c * 4 + 1] = a[0]! ^ xt(a[1]!) ^ (xt(a[2]!) ^ a[2]!) ^ a[3]!;
                s[c * 4 + 2] = a[0]! ^ a[1]! ^ xt(a[2]!) ^ (xt(a[3]!) ^ a[3]!);
                s[c * 4 + 3] = (xt(a[0]!) ^ a[0]!) ^ a[1]! ^ a[2]! ^ xt(a[3]!);
            }
        }
        for (let i = 0; i < 16; i++) s[i] = s[i]! ^ rk[r * 16 + i]!;
    }
    out.set(s);
}

/** AES-CBC decryption: the IV is the first 16 bytes unless given; `pad` strips PKCS#7 padding. */
export function aesCbcDecrypt(key: Uint8Array, data: Uint8Array, iv?: Uint8Array, pad = true): Uint8Array {
    let start = 0;
    if (!iv) { iv = data.subarray(0, 16); start = 16; }
    const n = Math.floor((data.length - start) / 16);
    const { rk, rounds } = expandKey(key);
    const out = new Uint8Array(n * 16);
    const blk = new Uint8Array(16);
    let prev = iv;
    for (let i = 0; i < n; i++) {
        decryptBlock(rk, rounds, data, start + i * 16, blk);
        for (let k = 0; k < 16; k++) out[i * 16 + k] = blk[k]! ^ prev[k]!;
        prev = data.subarray(start + i * 16, start + i * 16 + 16);
    }
    if (pad && out.length) { const p = out[out.length - 1]!; if (p >= 1 && p <= 16) return out.subarray(0, out.length - p); }
    return out;
}

export function aesCbcEncrypt(key: Uint8Array, data: Uint8Array, iv: Uint8Array): Uint8Array {
    const { rk, rounds } = expandKey(key);
    const out = new Uint8Array(data.length);
    let prev = iv;
    const blk = new Uint8Array(16), x = new Uint8Array(16);
    for (let i = 0; i + 16 <= data.length; i += 16) {
        for (let k = 0; k < 16; k++) x[k] = data[i + k]! ^ prev[k]!;
        encryptBlock(rk, rounds, x, 0, blk);
        out.set(blk, i);
        prev = out.subarray(i, i + 16);
    }
    return out;
}

// ---- SHA-256, SHA-384, SHA-512 (for the revision 5 and 6 key derivation) --------------------------------------------------------------
const K256 = new Uint32Array([0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2]);

export function sha256(data: Uint8Array): Uint8Array {
    const n = data.length;
    const padded = new Uint8Array(((n + 9 + 63) >> 6) << 6);
    padded.set(data);
    padded[n] = 0x80;
    const dv = new DataView(padded.buffer);
    dv.setUint32(padded.length - 4, (n << 3) >>> 0);
    dv.setUint32(padded.length - 8, Math.floor(n / 0x20000000));
    const h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);
    const w = new Uint32Array(64);
    const rotr = (x: number, k: number) => (x >>> k) | (x << (32 - k));
    for (let off = 0; off < padded.length; off += 64) {
        for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4);
        for (let i = 16; i < 64; i++) {
            const s0 = rotr(w[i - 15]!, 7) ^ rotr(w[i - 15]!, 18) ^ (w[i - 15]! >>> 3);
            const s1 = rotr(w[i - 2]!, 17) ^ rotr(w[i - 2]!, 19) ^ (w[i - 2]! >>> 10);
            w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
        }
        let [a, b, c, d, e, f, g, hh] = h as unknown as number[];
        for (let i = 0; i < 64; i++) {
            const S1 = rotr(e!, 6) ^ rotr(e!, 11) ^ rotr(e!, 25);
            const ch = (e! & f!) ^ (~e! & g!);
            const t1 = (hh! + S1 + ch + K256[i]! + w[i]!) >>> 0;
            const S0 = rotr(a!, 2) ^ rotr(a!, 13) ^ rotr(a!, 22);
            const mj = (a! & b!) ^ (a! & c!) ^ (b! & c!);
            const t2 = (S0 + mj) >>> 0;
            hh = g; g = f; f = e; e = (d! + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
        }
        h[0] = (h[0]! + a!) >>> 0; h[1] = (h[1]! + b!) >>> 0; h[2] = (h[2]! + c!) >>> 0; h[3] = (h[3]! + d!) >>> 0;
        h[4] = (h[4]! + e!) >>> 0; h[5] = (h[5]! + f!) >>> 0; h[6] = (h[6]! + g!) >>> 0; h[7] = (h[7]! + hh!) >>> 0;
    }
    const out = new Uint8Array(32);
    const o = new DataView(out.buffer);
    h.forEach((v, i) => o.setUint32(i * 4, v));
    return out;
}

const K512 = [
    "428a2f98d728ae22", "7137449123ef65cd", "b5c0fbcfec4d3b2f", "e9b5dba58189dbbc", "3956c25bf348b538", "59f111f1b605d019", "923f82a4af194f9b", "ab1c5ed5da6d8118", "d807aa98a3030242", "12835b0145706fbe", "243185be4ee4b28c", "550c7dc3d5ffb4e2", "72be5d74f27b896f", "80deb1fe3b1696b1", "9bdc06a725c71235", "c19bf174cf692694",
    "e49b69c19ef14ad2", "efbe4786384f25e3", "0fc19dc68b8cd5b5", "240ca1cc77ac9c65", "2de92c6f592b0275", "4a7484aa6ea6e483", "5cb0a9dcbd41fbd4", "76f988da831153b5", "983e5152ee66dfab", "a831c66d2db43210", "b00327c898fb213f", "bf597fc7beef0ee4", "c6e00bf33da88fc2", "d5a79147930aa725", "06ca6351e003826f", "142929670a0e6e70",
    "27b70a8546d22ffc", "2e1b21385c26c926", "4d2c6dfc5ac42aed", "53380d139d95b3df", "650a73548baf63de", "766a0abb3c77b2a8", "81c2c92e47edaee6", "92722c851482353b", "a2bfe8a14cf10364", "a81a664bbc423001", "c24b8b70d0f89791", "c76c51a30654be30", "d192e819d6ef5218", "d69906245565a910", "f40e35855771202a", "106aa07032bbd1b8",
    "19a4c116b8d2d0c8", "1e376c085141ab53", "2748774cdf8eeb99", "34b0bcb5e19b48a8", "391c0cb3c5c95a63", "4ed8aa4ae3418acb", "5b9cca4f7763e373", "682e6ff3d6b2b8a3", "748f82ee5defb2fc", "78a5636f43172f60", "84c87814a1f0ab72", "8cc702081a6439ec", "90befffa23631e28", "a4506cebde82bde9", "bef9a3f7b2c67915", "c67178f2e372532b",
    "ca273eceea26619c", "d186b8c721c0c207", "eada7dd6cde0eb1e", "f57d4f7fee6ed178", "06f067aa72176fba", "0a637dc5a2c898a6", "113f9804bef90dae", "1b710b35131c471b", "28db77f523047d84", "32caab7b40c72493", "3c9ebe0a15c9bebc", "431d67c49c100d4c", "4cc5d4becb3e42b6", "597f299cfc657e2a", "5fcb6fab3ad6faec", "6c44198c4a475817"
].map((s) => BigInt("0x" + s));
const M64 = (1n << 64n) - 1n;

function sha512core(data: Uint8Array, init: bigint[], outLen: number): Uint8Array {
    const n = data.length;
    const padded = new Uint8Array(((n + 17 + 127) >> 7) << 7);
    padded.set(data);
    padded[n] = 0x80;
    const dv = new DataView(padded.buffer);
    dv.setUint32(padded.length - 4, (n << 3) >>> 0);
    dv.setUint32(padded.length - 8, Math.floor(n / 0x20000000));
    const h = init.slice();
    const w = new Array<bigint>(80);
    const rotr = (x: bigint, k: bigint) => ((x >> k) | (x << (64n - k))) & M64;
    for (let off = 0; off < padded.length; off += 128) {
        for (let i = 0; i < 16; i++) w[i] = (BigInt(dv.getUint32(off + i * 8)) << 32n) | BigInt(dv.getUint32(off + i * 8 + 4));
        for (let i = 16; i < 80; i++) {
            const s0 = rotr(w[i - 15]!, 1n) ^ rotr(w[i - 15]!, 8n) ^ (w[i - 15]! >> 7n);
            const s1 = rotr(w[i - 2]!, 19n) ^ rotr(w[i - 2]!, 61n) ^ (w[i - 2]! >> 6n);
            w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) & M64;
        }
        let [a, b, c, d, e, f, g, hh] = h as bigint[];
        for (let i = 0; i < 80; i++) {
            const S1 = rotr(e!, 14n) ^ rotr(e!, 18n) ^ rotr(e!, 41n);
            const ch = (e! & f!) ^ (~e! & M64 & g!);
            const t1 = (hh! + S1 + ch + K512[i]! + w[i]!) & M64;
            const S0 = rotr(a!, 28n) ^ rotr(a!, 34n) ^ rotr(a!, 39n);
            const mj = (a! & b!) ^ (a! & c!) ^ (b! & c!);
            const t2 = (S0 + mj) & M64;
            hh = g; g = f; f = e; e = (d! + t1) & M64; d = c; c = b; b = a; a = (t1 + t2) & M64;
        }
        const add = [a, b, c, d, e, f, g, hh] as bigint[];
        for (let i = 0; i < 8; i++) h[i] = (h[i]! + add[i]!) & M64;
    }
    const out = new Uint8Array(64);
    h.forEach((v, i) => { for (let k = 0; k < 8; k++) out[i * 8 + k] = Number((v >> BigInt(56 - 8 * k)) & 255n); });
    return out.subarray(0, outLen);
}

const IV512 = ["6a09e667f3bcc908", "bb67ae8584caa73b", "3c6ef372fe94f82b", "a54ff53a5f1d36f1", "510e527fade682d1", "9b05688c2b3e6c1f", "1f83d9abfb41bd6b", "5be0cd19137e2179"].map((s) => BigInt("0x" + s));
const IV384 = ["cbbb9d5dc1059ed8", "629a292a367cd507", "9159015a3070dd17", "152fecd8f70e5939", "67332667ffc00b31", "8eb44a8768581511", "db0c2e0d64f98fa7", "47b5481dbefa4fa4"].map((s) => BigInt("0x" + s));
export const sha512 = (d: Uint8Array) => sha512core(d, IV512, 64);
export const sha384 = (d: Uint8Array) => sha512core(d, IV384, 48);

/** ISO 32000-2 algorithm 2.B: the revision 6 hash. */
function hash2B(password: Uint8Array, salt: Uint8Array, udata: Uint8Array): Uint8Array {
    let k = sha256(concat(password, salt, udata));
    for (let i = 0; ; i++) {
        const k1 = concat(password, k, udata);
        const rep = new Uint8Array(k1.length * 64);
        for (let r = 0; r < 64; r++) rep.set(k1, r * k1.length);
        const e = aesCbcEncrypt(k.subarray(0, 16), rep, k.subarray(16, 32));
        let mod = 0;
        for (let q = 0; q < 16; q++) mod = (mod * 256 + e[q]!) % 3;
        k = mod === 0 ? sha256(e) : mod === 1 ? sha384(e) : sha512(e);
        if (i >= 63 && e[e.length - 1]! <= i - 32) break;
    }
    return k.subarray(0, 32);
}

const concat = (...a: Uint8Array[]): Uint8Array => { const n = a.reduce((s, x) => s + x.length, 0); const o = new Uint8Array(n); let p = 0; for (const x of a) { o.set(x, p); p += x.length; } return o; };
const bytesOf = (s: string): Uint8Array => { const o = new Uint8Array(s.length); for (let i = 0; i < s.length; i++) o[i] = s.charCodeAt(i) & 255; return o; };
const strOf = (b: Uint8Array): string => { let r = ""; for (let i = 0; i < b.length; i += 8192) r += String.fromCharCode(...b.subarray(i, i + 8192)); return r; };

const PAD = Uint8Array.from([0x28, 0xbf, 0x4e, 0x5e, 0x4e, 0x75, 0x8a, 0x41, 0x64, 0x00, 0x4e, 0x56, 0xff, 0xfa, 0x01, 0x08, 0x2e, 0x2e, 0x00, 0xb6, 0xd0, 0x68, 0x3e, 0x80, 0x2f, 0x0c, 0xa9, 0xfe, 0x64, 0x53, 0x69, 0x7a]);

/** Thrown when a file needs a password (none given, or the wrong one), so a viewer can ask for it. */
export class PasswordRequired extends Error {
    constructor(readonly wrong: boolean) {
        super(wrong ? "That password is not correct." : "This PDF needs a password to open.");
        this.name = "PasswordRequired";
    }
}

/** Sets up decryption from the trailer's /Encrypt with the user password (empty by default); null when the file is not encrypted. Throws PasswordRequired when the password is needed or wrong. */
export function makeCipher(xref: XRef, password = ""): Cipher | null {
    const enc = xref.trailer.get("Encrypt");
    if (!(enc instanceof Dict)) return null;
    const encRef = xref.trailer.raw("Encrypt");
    const filter = enc.name("Filter");
    if (filter && filter !== "Standard") throw new Error(`Unsupported security handler ${filter}.`);
    const V = enc.num("V"), R = enc.num("R"), P = enc.num("P") | 0;
    const O = bytesOf(enc.str("O") ?? ""), U = bytesOf(enc.str("U") ?? "");
    const idArr = xref.trailer.arr("ID");
    const id0 = idArr && typeof idArr[0] === "string" ? bytesOf(idArr[0]) : new Uint8Array(0);
    const encryptMeta = enc.get("EncryptMetadata") !== false;
    type Method = "RC4" | "AESV2" | "AESV3" | "None";
    let method: Method = "RC4";
    let strMethod: Method = method;
    let keyLen = Math.max(5, Math.min(16, (enc.num("Length", 40) || 40) / 8));
    if (V >= 4) {
        const cf = enc.dict("CF");
        const pick = (nm: string | null) => {
            if (!nm || nm === "Identity") return "None" as const;
            const d = cf?.dict(nm);
            const cfm = d?.name("CFM");
            if (d && d.num("Length")) keyLen = d.num("Length") > 40 ? d.num("Length") / 8 : d.num("Length");
            return cfm === "AESV2" ? "AESV2" : cfm === "AESV3" ? "AESV3" : cfm === "V2" ? "RC4" : "None";
        };
        method = pick(enc.name("StmF"));
        strMethod = pick(enc.name("StrF"));
        if (V === 4) keyLen = 16;
    }
    let key: Uint8Array;
    if (R >= 5) {
        const UE = bytesOf(enc.str("UE") ?? "");
        const vsalt = U.subarray(32, 40), ksalt = U.subarray(40, 48);
        // PDF 2.0 / AES-256: the password as UTF-8, at most 127 bytes
        const pw = new TextEncoder().encode(password).subarray(0, 127);
        const ik = R === 5 ? sha256(concat(pw, ksalt)) : hash2B(pw, ksalt, new Uint8Array(0));
        const test = R === 5 ? sha256(concat(pw, vsalt)) : hash2B(pw, vsalt, new Uint8Array(0));
        if (strOf(test) !== strOf(U.subarray(0, 32))) throw new PasswordRequired(password !== "");
        key = aesCbcDecrypt(ik, UE.subarray(0, 32), new Uint8Array(16), false);
    } else {
        const pbytes = new Uint8Array(4);
        new DataView(pbytes.buffer).setInt32(0, P, true);
        // the user password, padded or cut to 32 bytes with the standard pad string
        const given = bytesOf(password);
        const padded = concat(given.subarray(0, 32), PAD).subarray(0, 32);
        let input = concat(padded, O.subarray(0, 32), pbytes, id0, R >= 4 && !encryptMeta ? Uint8Array.from([255, 255, 255, 255]) : new Uint8Array(0));
        let h = md5(input);
        const n = R === 2 ? 5 : keyLen;
        if (R >= 3) for (let i = 0; i < 50; i++) h = md5(h.subarray(0, n));
        key = h.subarray(0, n);
        // check the empty user password
        let u: Uint8Array;
        if (R === 2) u = rc4(key, PAD);
        else {
            let x = md5(concat(PAD, id0));
            x = rc4(key, x);
            for (let i = 1; i <= 19; i++) x = rc4(key.map((b) => b ^ i), x);
            u = x;
        }
        const ok = R === 2 ? strOf(u) === strOf(U.subarray(0, 32)) : strOf(u.subarray(0, 16)) === strOf(U.subarray(0, 16));
        if (!ok) throw new PasswordRequired(password !== "");
        input = new Uint8Array(0);
    }
    const objKey = (num: number, gen: number, m: string): Uint8Array => {
        if (m === "AESV3") return key;
        const ext = new Uint8Array([num & 255, (num >> 8) & 255, (num >> 16) & 255, gen & 255, (gen >> 8) & 255, ...(m === "AESV2" ? [0x73, 0x41, 0x6c, 0x54] : [])]);
        return md5(concat(key, ext)).subarray(0, Math.min(key.length + 5, 16));
    };
    const run = (m: string, num: number, gen: number, data: Uint8Array): Uint8Array => {
        if (m === "None") return data;
        const k = objKey(num, gen, m);
        if (m === "RC4") return rc4(k, data);
        if (data.length < 32) return data.length < 16 ? data : aesCbcDecrypt(k, data);
        return aesCbcDecrypt(k, data);
    };
    const encNum = encRef instanceof PRef ? encRef.num : -1;
    void PName;
    return {
        string: (num, gen, s) => (num === encNum ? s : strOf(run(strMethod, num, gen, bytesOf(s)))),
        stream: (num, gen, d) => (num === encNum ? d : run(method, num, gen, d))
    };
}
export type { PObj };
